import { z } from 'zod';
import { encodeBody } from '../http/encode.js';
import { barcodeCreateSchema, barcodeSchema, itemCreateSchema, itemGroupCreateSchema, itemGroupPatchSchema, itemGroupSchema, itemPatchSchema, itemSchema, itemVariantCreateSchema, itemVariantSchema, } from '../schemas/items.js';
const itemListSchema = z.array(itemSchema);
const groupListSchema = z.array(itemGroupSchema);
const variantListSchema = z.array(itemVariantSchema);
const barcodeListSchema = z.array(barcodeSchema);
export class ItemsApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    list(filter = {}, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/items',
            query: {
                search: filter.search,
                item_group_id: filter.item_group_id,
                item_type: filter.item_type,
                status: filter.status,
                is_stock_tracked: filter.is_stock_tracked,
                limit: filter.limit ?? 50,
                offset: filter.offset ?? 0,
            },
            schema: itemListSchema,
            signal,
        });
    }
    get(itemId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/items/${encodeURIComponent(itemId)}`,
            schema: itemSchema,
            signal,
        });
    }
    /** 404 when the code is unknown — an ordinary outcome at a counter, not an exception. */
    getByCode(itemCode, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/items/by-code',
            query: { code: itemCode },
            schema: itemSchema,
            signal,
        });
    }
    create(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/items',
            body: encodeBody(itemCreateSchema, payload, 'createItem'),
            operation,
            schema: itemSchema,
            signal,
        });
    }
    /**
     * Patches are applied with `exclude_unset`, never `exclude_none` — so sending `null`
     * clears a nullable field and omitting the key leaves it alone. Accounts uses
     * `exclude_none`, which makes it impossible to remove a description once set.
     */
    update(itemId, patch, operation, signal) {
        return this.transport.request({
            method: 'PATCH',
            path: `/v1/items/${encodeURIComponent(itemId)}`,
            body: encodeBody(itemPatchSchema, patch, 'updateItem'),
            operation,
            schema: itemSchema,
            signal,
        });
    }
    listVariants(itemId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/items/${encodeURIComponent(itemId)}/variants`,
            schema: variantListSchema,
            signal,
        });
    }
    createVariant(itemId, payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: `/v1/items/${encodeURIComponent(itemId)}/variants`,
            body: encodeBody(itemVariantCreateSchema, { ...payload, item_id: itemId }, 'createVariant'),
            operation,
            schema: itemVariantSchema,
            signal,
        });
    }
    listBarcodes(itemId, signal) {
        return this.transport.request({
            method: 'GET',
            path: `/v1/items/${encodeURIComponent(itemId)}/barcodes`,
            schema: barcodeListSchema,
            signal,
        });
    }
}
export class ItemGroupsApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    list(signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/item-groups',
            schema: groupListSchema,
            signal,
        });
    }
    create(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/item-groups',
            body: encodeBody(itemGroupCreateSchema, payload, 'createItemGroup'),
            operation,
            schema: itemGroupSchema,
            signal,
        });
    }
    /** A parent that would close a loop in the tree is refused with 422, not stored. */
    update(groupId, patch, operation, signal) {
        return this.transport.request({
            method: 'PATCH',
            path: `/v1/item-groups/${encodeURIComponent(groupId)}`,
            body: encodeBody(itemGroupPatchSchema, patch, 'updateItemGroup'),
            operation,
            schema: itemGroupSchema,
            signal,
        });
    }
}
export class BarcodesApi {
    transport;
    constructor(transport) {
        this.transport = transport;
    }
    /**
     * The scan path. A GET, so a handheld does not need an idempotency key to ask what a
     * barcode is.
     */
    resolve(barcode, signal) {
        return this.transport.request({
            method: 'GET',
            path: '/v1/barcodes/resolve',
            query: { barcode },
            schema: barcodeSchema,
            signal,
        });
    }
    create(payload, operation, signal) {
        return this.transport.request({
            method: 'POST',
            path: '/v1/barcodes',
            body: encodeBody(barcodeCreateSchema, payload, 'createBarcode'),
            operation,
            schema: barcodeSchema,
            signal,
        });
    }
}
//# sourceMappingURL=items.js.map